import type { DrawCreateEvent, DrawUpdateEvent } from "@mapbox/mapbox-gl-draw";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { createSharedComposable } from "@vueuse/core";
import type {
	Feature,
	Geometry as GeoGeometry,
	Geometry,
	LineString,
	MultiLineString,
	MultiPolygon,
	Polygon,
	Position,
} from "geojson";
import maplibregl, {
	type GeoJSONSource,
	type IControl,
	type ImageSource,
	type Map,
} from "maplibre-gl";
import {
	computed,
	inject,
	onMounted,
	onScopeDispose,
	onUnmounted,
	provide,
	ref,
	shallowReadonly,
	shallowRef,
	type App,
	type Ref,
	type ShallowRef,
} from "vue";
import { mapKey, setupGlobalMapKey } from "./keys";
import {
	isExtrusion,
	type Circle,
	type Extrusion,
	type Fill,
	type LineSymbol,
	type RasterOptions,
	type Stroke,
} from "./types";

// taken from https://wiki.openstreetmap.org/wiki/Zoom_levels
export const EARTH_CIRCUMFERENCE = 40075016.686; // circumference of the earth in meters
const TILE_SIZE = 256;
// MapLibre GL renders with 512px tiles internally, so map.getZoom() is on that scale
const GL_TILE_SIZE = 512;

export type LayerOrderAdd = (id: string, zIndex: number) => void;
export type LayerOrderRemove = (id: string) => void;

export type ProvideMapFn = (
	map: Map,
	layerOrderAdd: LayerOrderAdd,
	layerOrderRemove: LayerOrderRemove,
) => void;

export type ProvidedMap = {
	map: Map;
	layerOrderAdd: LayerOrderAdd;
	layerOrderRemove: LayerOrderRemove;
};

export function provideMapLocal() {
	const providedMap = shallowRef<ProvidedMap>();
	provide<ShallowRef<ProvidedMap | undefined>>(mapKey, providedMap);
	return providedMap;
}

export function useSetupMapGlobal() {
	return inject<ProvideMapFn | undefined>(setupGlobalMapKey, undefined);
}

export function provideMapGlobal(app: App) {
	const globalMap = shallowRef<ProvidedMap>();
	app.provide(
		setupGlobalMapKey,
		(
			map: Map,
			layerOrderAdd: LayerOrderAdd,
			layerOrderRemove: LayerOrderRemove,
		) => {
			globalMap.value = {
				map,
				layerOrderAdd,
				layerOrderRemove,
			};
		},
	);

	app.provide(mapKey, globalMap);
}

export function useMap(): {
	map: Map;
	layerOrderAdd: LayerOrderAdd;
	layerOrderRemove: LayerOrderRemove;
} {
	const providedMap = inject<ShallowRef<ProvidedMap>>(mapKey);

	if (!providedMap?.value) {
		throw new Error(
			"Maplibre components must be used inside a maplibre map or have a global map provided",
		);
	}

	return {
		map: providedMap.value.map,
		layerOrderAdd: providedMap.value.layerOrderAdd,
		layerOrderRemove: providedMap.value.layerOrderRemove,
	};
}

export function useGlobalMapRef(): Ref<Map | undefined> {
	const providedMap = inject<ShallowRef<ProvidedMap>>(mapKey);
	return computed(() => providedMap?.value?.map);
}

export function useDraw(styles?: object[]): {
	draw: MapboxDraw;
	loaded: Ref<boolean>;
} {
	const { map } = useMap();
	const loaded = ref(false);
	const draw = styles
		? new MapboxDraw({ displayControlsDefault: false, styles })
		: new MapboxDraw({ displayControlsDefault: false });

	onMounted(() => {
		map.addControl(draw as unknown as IControl, "top-right");
		loaded.value = true;
	});

	onUnmounted(() => {
		map.removeControl(draw as unknown as IControl);
	});
	return { draw, loaded };
}

export function toEquirectangular(
	point: Position,
	reference: Position,
): Position {
	const lng = point[0];
	const lat = point[1];

	// Convert longitude and latitude to radians
	const lngRad = (lng - reference[0]) * (Math.PI / 180);
	const latRad = lat * (Math.PI / 180);

	// Calculate Cartesian coordinates
	const y = Math.cos(latRad) * Math.cos(lngRad);
	const x = Math.cos(latRad) * Math.sin(lngRad);

	return [x, y];
}

export function mapGeometry<T extends Geometry>(
	geometry: T,
	predicate: (p: Position) => Position,
): T {
	if (geometry.type === "Point") {
		return {
			type: "Point",
			coordinates: predicate(geometry.coordinates),
		} as T;
	} else if (geometry.type === "MultiPoint") {
		return {
			type: "MultiPoint",
			coordinates: geometry.coordinates.map(predicate),
		} as T;
	} else if (geometry.type === "LineString") {
		return {
			type: "LineString",
			coordinates: geometry.coordinates.map(predicate),
		} as T;
	} else if (geometry.type === "MultiLineString") {
		return {
			type: "MultiLineString",
			coordinates: geometry.coordinates.map(line => line.map(predicate)),
		} as T;
	} else if (geometry.type === "Polygon") {
		return {
			type: "Polygon",
			coordinates: geometry.coordinates.map(ring => ring.map(predicate)),
		} as T;
	} else if (geometry.type === "MultiPolygon") {
		return {
			type: "MultiPolygon",
			coordinates: geometry.coordinates.map(polygon =>
				polygon.map(ring => ring.map(predicate)),
			),
		} as T;
	} else {
		return {
			type: "GeometryCollection",
			geometries: geometry.geometries.map(g => mapGeometry(g, predicate)),
		} as T;
	}
}

export function geometryToEquirectangular(
	geometry: LineString | MultiLineString | Polygon | MultiPolygon,
) {
	if (geometry.coordinates.length === 0) return geometry;

	let reference: Position = [0, 0];
	if (geometry.type === "Polygon" || geometry.type === "MultiLineString") {
		reference = geometry.coordinates[0][0];
	} else if (geometry.type === "LineString") {
		reference = geometry.coordinates[0];
	} else if (geometry.type === "MultiPolygon") {
		reference = geometry.coordinates[0][0][0];
	}

	return mapGeometry(geometry, p => toEquirectangular(p, reference));
}

export function useMapUtils(parent: Map, sourceId: string) {
	return {
		setGeoJsonData: (data: GeoJSON.GeoJSON, tolerance?: number) =>
			setGeoJsonData(parent, sourceId, data, tolerance),
		setImageData: (url: string, ne: Position, sw: Position) =>
			setImageData(parent, sourceId, url, ne, sw),
		addImage: (url: string) => addImage(parent, url),
		addLineLayer: (layerId: string, stroke: Stroke) =>
			addLineLayer(parent, layerId, sourceId, stroke),
		addFillLayer: (layerId: string, fill: Fill | Extrusion) =>
			addFillLayer(parent, layerId, sourceId, fill),
		addCircleLayer: (layerId: string, circle: Circle) =>
			addCircleLayer(parent, layerId, sourceId, circle),
		addImageLayer: (layerId: string, rasterOptions?: RasterOptions) =>
			addImageLayer(parent, layerId, sourceId, rasterOptions),
		addSymbolLayer: (layerId: string, symbol: LineSymbol) =>
			addSymbolLayer(parent, layerId, sourceId, symbol),
		removeLayer: (layerId: string) => {
			if (parent.getLayer(layerId)) {
				parent.removeLayer(layerId);
			}
		},
		removeSource: () => {
			if (parent.getSource(sourceId)) {
				parent.removeSource(sourceId);
			}
		},
	};
}

function setGeoJsonData(
	parent: Map,
	sourceId: string,
	data: GeoJSON.GeoJSON,
	tolerance?: number,
) {
	const source = parent.getSource(sourceId) as GeoJSONSource | undefined;
	if (!source) {
		parent.addSource(sourceId, {
			type: "geojson",
			data,
			...(tolerance == null ? {} : { tolerance }),
		});
	} else {
		source.setData(data);
	}
}

function setImageData(
	parent: Map,
	sourceId: string,
	url: string,
	[neLng, neLat]: Position,
	[swLng, swLat]: Position,
) {
	const source = parent.getSource(sourceId) as ImageSource | undefined;
	if (!source) {
		parent.addSource(sourceId, {
			type: "image",
			url,
			coordinates: [
				[swLng, neLat],
				[neLng, neLat],
				[neLng, swLat],
				[swLng, swLat],
			],
		});
		const newSource = parent.getSource(sourceId) as ImageSource;
		newSource.once("data", () => {
			URL.revokeObjectURL(url);
		});
	} else {
		source.updateImage({
			url,
			coordinates: [
				[swLng, neLat],
				[neLng, neLat],
				[neLng, swLat],
				[swLng, swLat],
			],
		});
		source.once("data", () => {
			URL.revokeObjectURL(url);
		});
	}
}

function addLineLayer(
	parent: Map,
	layerId: string,
	sourceId: string,
	stroke: Stroke,
) {
	const layer = parent.getLayer(layerId);
	if (layer) {
		parent.setPaintProperty(layerId, "line-color", stroke.color);
		parent.setPaintProperty(layerId, "line-width", stroke.weight);
		parent.setPaintProperty(layerId, "line-opacity", stroke.opacity);
		parent.setLayoutProperty(layerId, "line-cap", stroke.cap ?? "butt");
		if (stroke.dashArray)
			parent.setPaintProperty(
				layerId,
				"line-dasharray",
				stroke.dashArray,
			);
		parent.setLayerZoomRange(
			layerId,
			stroke.minZoom ?? 0,
			stroke.maxZoom ?? 24,
		);
		return;
	}

	const paint = stroke.dashArray
		? {
				"line-color": stroke.color,
				"line-width": stroke.weight,
				"line-opacity": stroke.opacity,
				"line-dasharray": stroke.dashArray,
			}
		: {
				"line-color": stroke.color,
				"line-width": stroke.weight,
				"line-opacity": stroke.opacity,
			};

	parent.addLayer({
		id: layerId,
		type: "line",
		source: sourceId,
		layout: { "line-cap": stroke.cap ?? "butt" },
		minzoom: stroke.minZoom ?? 0,
		maxzoom: stroke.maxZoom ?? 24,
		paint,
	});
}

function addSymbolLayer(
	parent: Map,
	layerId: string,
	sourceId: string,
	symbol: LineSymbol,
) {
	const layer = parent.getLayer(layerId);
	if (layer) {
		return;
	}

	parent.addLayer({
		id: layerId,
		type: "symbol",
		source: sourceId,
		minzoom: symbol.minZoom ?? 0,
		maxzoom: symbol.maxZoom ?? 24,
		layout: {
			"text-font": ["Arial Unicode MS Bold"],
			"symbol-placement": "line",
			"text-size": symbol.size ?? 24,
			"text-anchor": "center",
			"text-letter-spacing": 0,
			"text-field": symbol.symbol ?? "▶",
			"text-allow-overlap": true,
			"text-keep-upright": false,
			"text-padding": 0,
			"text-offset": symbol.offset ?? [0, 0],
			"symbol-spacing": symbol.spacing ?? 100,
		},
		paint: {
			"text-color": symbol.color,
		},
	});
}

function addCircleLayer(
	parent: Map,
	layerId: string,
	sourceId: string,
	circle: Circle,
) {
	const layer = parent.getLayer(layerId);
	if (layer) {
		parent.setPaintProperty(layerId, "circle-color", circle.color);
		parent.setPaintProperty(layerId, "circle-radius", circle.radius);
		parent.setPaintProperty(layerId, "circle-opacity", circle.opacity);
		parent.setLayerZoomRange(
			layerId,
			circle.minZoom ?? 0,
			circle.maxZoom ?? 24,
		);
		if (circle.stroke) {
			parent.setPaintProperty(
				layerId,
				"circle-stroke-color",
				circle.stroke.color,
			);
			parent.setPaintProperty(
				layerId,
				"circle-stroke-width",
				circle.stroke.weight,
			);
			parent.setPaintProperty(
				layerId,
				"circle-stroke-opacity",
				circle.stroke.opacity,
			);
			parent.setPaintProperty(
				layerId,
				"circle-stroke-dasharray",
				circle.stroke.dashArray ?? undefined,
			);
		}
		return;
	}
	parent.addLayer({
		id: layerId,
		type: "circle",
		source: sourceId,
		layout: {},
		minzoom: circle.minZoom ?? 0,
		maxzoom: circle.maxZoom ?? 24,
		paint: {
			"circle-color": circle.color,
			"circle-radius": circle.radius,
			"circle-opacity": circle.opacity,
		},
	});
	if (circle.stroke) {
		parent.setPaintProperty(
			layerId,
			"circle-stroke-color",
			circle.stroke.color,
		);
		parent.setPaintProperty(
			layerId,
			"circle-stroke-width",
			circle.stroke.weight,
		);
		parent.setPaintProperty(
			layerId,
			"circle-stroke-opacity",
			circle.stroke.opacity,
		);
	}
}

const pendingImageLoads = new WeakMap<Map, Record<string, Promise<void>>>();

// Images are registered on the map under their url, so identical paths are
// only loaded once per map, including concurrent requests.
export async function addImage(parent: Map, url: string) {
	if (parent.hasImage(url)) return url;

	let loads = pendingImageLoads.get(parent);
	if (!loads) {
		loads = {};
		pendingImageLoads.set(parent, loads);
	}

	const pendingLoads = loads;
	pendingLoads[url] ??= parent
		.loadImage(url)
		.then(image => {
			if (image && !parent.hasImage(url)) parent.addImage(url, image.data);
		})
		.finally(() => {
			delete pendingLoads[url];
		});

	await pendingLoads[url];
	return url;
}

function addFillLayer(
	parent: Map,
	layerId: string,
	sourceId: string,
	fill: Fill | Extrusion,
) {
	const layer = parent.getLayer(layerId);

	if (layer) {
		parent.setPaintProperty(layerId, "fill-opacity", fill.opacity);
		parent.setLayerZoomRange(
			layerId,
			fill.minZoom ?? 0,
			fill.maxZoom ?? 24,
		);
		if (isExtrusion(fill)) {
			if (fill.pattern)
				parent.setPaintProperty(
					layerId,
					"fill-extrusion-pattern",
					fill.pattern,
				);
			else
				parent.setPaintProperty(
					layerId,
					"fill-extrusion-color",
					fill.color,
				);
		} else {
			if (fill.pattern)
				parent.setPaintProperty(layerId, "fill-pattern", fill.pattern);
			else parent.setPaintProperty(layerId, "fill-color", fill.color);
		}
		return;
	}
	if (isExtrusion(fill)) {
		if (fill.pattern)
			parent.addLayer({
				id: layerId,
				type: "fill-extrusion",
				source: sourceId,
				layout: {},
				minzoom: fill.minZoom ?? 0,
				maxzoom: fill.maxZoom ?? 24,
				paint: {
					"fill-extrusion-opacity": fill.opacity,
					"fill-extrusion-pattern": fill.pattern,
					"fill-extrusion-height": fill.height,
					"fill-extrusion-base": fill.base ?? 0,
				},
			});
		else
			parent.addLayer({
				id: layerId,
				type: "fill-extrusion",
				source: sourceId,
				layout: {},
				paint: {
					"fill-extrusion-color": fill.color,
					"fill-extrusion-opacity": fill.opacity,
					"fill-extrusion-height": fill.height,
					"fill-extrusion-base": fill.base ?? 0,
				},
			});
	} else {
		if (fill.pattern)
			parent.addLayer({
				id: layerId,
				type: "fill",
				source: sourceId,
				layout: {},
				minzoom: fill.minZoom ?? 0,
				maxzoom: fill.maxZoom ?? 24,
				paint: {
					"fill-opacity": fill.opacity,
					"fill-pattern": fill.pattern,
				},
			});
		else
			parent.addLayer({
				id: layerId,
				type: "fill",
				source: sourceId,
				layout: {},
				paint: {
					"fill-color": fill.color,
					"fill-opacity": fill.opacity,
				},
			});
	}
}

function addImageLayer(
	parent: Map,
	layerId: string,
	sourceId: string,
	rasterOptions?: RasterOptions,
) {
	const layer = parent.getLayer(layerId);
	if (layer) {
		parent.setPaintProperty(
			layerId,
			"raster-opacity",
			rasterOptions?.opacity ?? 1,
		);
		parent.setPaintProperty(
			layerId,
			"raster-fade-duration",
			rasterOptions?.fadeDuration ?? 0,
		);
		parent.setLayerZoomRange(
			layerId,
			rasterOptions?.minZoom ?? 0,
			rasterOptions?.maxZoom ?? 24,
		);
		return;
	}
	parent.addLayer({
		id: layerId,
		type: "raster",
		source: sourceId,
		layout: {},
		maxzoom: rasterOptions?.maxZoom ?? 24,
		minzoom: rasterOptions?.minZoom ?? 0,
		paint: {
			"raster-fade-duration": rasterOptions?.fadeDuration ?? 0,
			"raster-opacity": rasterOptions?.opacity ?? 1,
		},
	});
}

export function useDrawGeometry<Geometry extends GeoGeometry>(
	setValue: (feature: Feature<Geometry>) => void,
	styles?: object[],
) {
	const { map } = useMap();
	const { draw, loaded } = useDraw(styles);

	let geojson: Feature<Geometry> | undefined;

	map.on("draw.update", update);
	function update(e: DrawUpdateEvent) {
		if (!geojson) return;
		const feature = e.features.find(
			f => f.id === geojson?.id,
		) as Feature<Geometry>;
		if (!feature) return;
		setValue(feature);
	}

	map.on("draw.create", create);
	function create(e: DrawCreateEvent) {
		const feature = e.features[0] as Feature<Geometry>;
		if (!feature) return;
		geojson = feature;
		setValue(feature);
	}

	onUnmounted(() => {
		map.off("draw.update", update);
	});

	return {
		updateGeometry: (geometry: Geometry, empty: boolean) => {
			if (geojson) {
				geojson.geometry = geometry;
				draw.add(geojson);
				return;
			}
			geojson = {
				type: "Feature",
				properties: {},
				geometry,
			};
			const [id] = draw.add(geojson);
			geojson.id = id;
			if (empty) return;
			draw.changeMode("direct_select", {
				featureId: geojson.id as string,
			});
		},
		draw,
		loaded,
	};
}

const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
	const openRequest = indexedDB.open("mapTileDb", 1);

	openRequest.onupgradeneeded = (e: IDBVersionChangeEvent) => {
		const result = (e.target as unknown as { result: IDBDatabase })?.result;
		if (result && !result.objectStoreNames.contains("store")) {
			result.createObjectStore("mapTiles");
			resolve(result);
		}
	};

	openRequest.onsuccess = e => {
		resolve((e.target as unknown as { result: IDBDatabase })?.result);
	};
	openRequest.onerror = e => {
		reject(e);
	};
	openRequest.onblocked = () => {
		reject(
			new Error(
				"IndexedDB open request is blocked. Please close other tabs using the same database.",
			),
		);
	};
});

async function addItem<T>(key: string, tile: T): Promise<void> {
	const db = await dbPromise;
	return new Promise((resolve, reject) => {
		const transaction = db.transaction("mapTiles", "readwrite");
		const store = transaction.objectStore("mapTiles");

		const request = store.put(tile, key);

		request.onerror = e => {
			reject(
				(e.target as unknown as { error: { name: string } }).error.name,
			);
		};
		request.onsuccess = () => {
			resolve();
		};
	});
}

async function getItem<T>(key: string): Promise<T> {
	const db = await dbPromise;
	return new Promise((resolve, reject) => {
		const transaction = db.transaction("mapTiles", "readonly");
		const store = transaction.objectStore("mapTiles");

		const request = store.get(key);

		request.onerror = e => {
			reject(
				(e.target as unknown as { error: { name: string } }).error.name,
			);
		};
		request.onsuccess = e => {
			resolve((e.target as unknown as { result: T }).result);
		};
	});
}

export async function clearMapCache() {
	const db = await dbPromise;
	return new Promise<void>((resolve, reject) => {
		const transaction = db.transaction("mapTiles", "readwrite");
		const store = transaction.objectStore("mapTiles");

		const request = store.clear();

		request.onerror = e => {
			reject(
				(e.target as unknown as { error: { name: string } }).error.name,
			);
		};
		request.onsuccess = () => {
			resolve();
		};
	});
}

export const clearTiles = clearMapCache;

// project and toTileCoords taken from https://developers.google.com/maps/documentation/javascript/examples/map-coordinates
function project(lat: number, lng: number) {
	let siny = Math.sin((lat * Math.PI) / 180);

	// Truncating to 0.9999 effectively limits latitude to 89.189. This is
	// about a third of a tile past the edge of the world tile.
	siny = Math.min(Math.max(siny, -0.9999), 0.9999);

	return [
		TILE_SIZE * (0.5 + lng / 360),
		TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
	];
}

function toTileCoords(lat: number, lng: number, zoom: number) {
	const scale = 1 << zoom;
	const [worldX, worldY] = project(lat, lng);

	return {
		x: Math.floor((worldX * scale) / TILE_SIZE),
		y: Math.floor((worldY * scale) / TILE_SIZE),
		z: zoom + 1,
	};
}

const MIN_STORAGE_ZOOM_LEVEL = 10;
const MAX_STORAGE_ZOOM_LEVEL = 18;

async function fetchData(url: string) {
	const response = await fetch(url);
	if (response.status !== 200) {
		throw new Error(`Tile fetch error: ${response.statusText}`);
	}
	const contentType = response.headers.get("content-type");
	if (!contentType) throw new Error(`No content-type header found`);
	if (
		contentType.includes("image/png") ||
		contentType.includes("image/jpeg") ||
		contentType.includes("application/octet-stream")
	)
		return response.arrayBuffer();
	else if (contentType.includes("application/json")) return response.json();
	else throw new Error(`Unsupported content-type: ${contentType}`);
}

export function saveTiles(
	getUrl: (x: number, y: number, z: number) => string,
	map: Map,
): {
	numTiles: number;
	save: (updateProgress?: (progress: number) => void) => Promise<void>;
} {
	const bounds = map.getBounds();

	const tiles: string[] = [];
	for (let z = MIN_STORAGE_ZOOM_LEVEL; z <= MAX_STORAGE_ZOOM_LEVEL; z++) {
		const nw = toTileCoords(bounds.getNorth(), bounds.getWest(), z);
		const se = toTileCoords(bounds.getSouth(), bounds.getEast(), z);
		for (let x = nw.x; x <= se.x; x++) {
			for (let y = nw.y; y <= se.y; y++) {
				const url = getUrl(x, y, z);
				tiles.push(url);
			}
		}
	}
	return {
		numTiles: tiles.length,
		save: async (updateProgress?: (progress: number) => void) => {
			let i = 0;
			return Promise.all(
				tiles.map(async url => {
					const tile = await fetchData(url);
					await addItem(url, tile);
					i += 1;
					updateProgress?.(i / tiles.length);
				}),
			).then(() => {
				return;
			});
		},
	};
}

async function getData(
	url: string,
	autoCacheTiles: boolean,
	offlineMode: boolean,
) {
	const offlineTile = await getItem(url);
	if (offlineTile) {
		return offlineTile;
	} else if (offlineMode) {
		// Do not try fetching tiles in offline mode
		throw new Error("Map tile not available");
	} else {
		const tile = await fetchData(url);

		// The ESRI api returns a grey tile when a tile is not available
		// By 'throwing' these tiles we can force MapLibre to use the tiles from the previous zoom level
		if (
			typeof tile === "object" &&
			tile instanceof ArrayBuffer &&
			tile.byteLength === 2521
		)
			throw new Error("Map tile not available");

		if (autoCacheTiles) addItem(url, tile);

		return tile;
	}
}

export function useOffline(autoCacheTiles = false, offlineMode = false) {
	if (!("indexedDB" in window)) {
		console.log("This browser doesn't support IndexedDB");
		return;
	}

	maplibregl.addProtocol("offline", async params => {
		try {
			const data = await getData(
				`https://${params.url.split("://")[1]}`,
				autoCacheTiles,
				offlineMode,
			);
			return { data };
		} catch (e) {
			if ((e as Error).message === "Map tile not available")
				throw { status: 404 } as unknown as Error;
			else throw e;
		}
	});
}

export const isPolygonClosed = (points: Position[][]) =>
	points.some(
		ring =>
			ring.length &&
			(ring[0][0] !== ring[ring.length - 1][0] ||
				ring[0][1] !== ring[ring.length - 1][1]),
	);

function useMapZoomInternal() {
	const { map } = useMap();
	const zoom = ref(map.getZoom());
	const subscription = map.on("zoom", () => (zoom.value = map.getZoom()));
	onScopeDispose(() => subscription.unsubscribe());

	return shallowReadonly(zoom);
}

function useMapRotationInternal() {
	const { map } = useMap();
	const rotation = ref(map.getBearing());
	const subscription = map.on(
		"rotate",
		() => (rotation.value = map.getBearing()),
	);
	onScopeDispose(() => subscription.unsubscribe());

	return rotation;
}

function useMapCenterInternal() {
	const { map } = useMap();
	const center = ref(map.getCenter());
	const subscription = map.on("move", () => (center.value = map.getCenter()));
	onScopeDispose(() => subscription.unsubscribe());

	return shallowReadonly(center);
}

function useMapBoundsInternal() {
	const { map } = useMap();
	const bounds = shallowRef(map.getBounds());
	const updateBounds = () => {
		bounds.value = map.getBounds();
	};
	const moveEndSubscription = map.on("moveend", updateBounds);
	const resizeSubscription = map.on("resize", updateBounds);
	onScopeDispose(() => {
		moveEndSubscription.unsubscribe();
		resizeSubscription.unsubscribe();
	});

	return shallowReadonly(bounds);
}

export const useMapBounds = createSharedComposable(useMapBoundsInternal);
export const useMapZoom = createSharedComposable(useMapZoomInternal);
export const useMapRotation = createSharedComposable(useMapRotationInternal);
export const useMapCenter = createSharedComposable(useMapCenterInternal);

export function useMapPixelsPerMeter() {
	const { map } = useMap();
	const zoom = useMapZoom();
	// Latitude barely shifts within a viewport, so track it on moveend/resize
	// rather than move to avoid recomputing on every pan/fly frame.
	const latitude = ref(map.getCenter().lat);
	const updateLatitude = () => {
		latitude.value = map.getCenter().lat;
	};
	const moveEndSubscription = map.on("moveend", updateLatitude);
	const resizeSubscription = map.on("resize", updateLatitude);
	onScopeDispose(() => {
		moveEndSubscription.unsubscribe();
		resizeSubscription.unsubscribe();
	});

	return computed(() => {
		// Total width of the map world in pixels at the current zoom level
		const worldSizeInPixels = GL_TILE_SIZE * Math.pow(2, zoom.value);

		// Meters per pixel at the current latitude
		const latitudeInRadians = latitude.value * (Math.PI / 180);
		const metersPerPixel =
			(EARTH_CIRCUMFERENCE * Math.cos(latitudeInRadians)) /
			worldSizeInPixels;

		// Invert to get pixels per meter
		return 1 / metersPerPixel;
	});
}
